// Service accounts bypass per-user overrides by design — see packages/permissions/AGENTS.md.
import { JOB_NIMBUS_USER_ID } from "@remi/constants";
import { ENSITE_ORG, INSPECTIFY_ORG } from "@remi/types/organizations";
import { PermissionRole } from "@remi/types/permissions";

import type { ContextUser } from "./context-user";
import { expandRolesToCapabilities } from "./utils/types";

export const SUNRUN_ORG_ID = "sunrun";
export const REMI_AUTOMATIONS_USER_ID = "remi-automations";
const REMI_WEBSITE_USER_ID = "remi_website";
const REMI_HUBSPOT_USER_ID = "remi-hubspot";

const WESTHILL_ORG_ID = "westhill";
const OPENLY_ORG_ID = "openly";
const AMFAM_ORG_ID = "american_family";
const ENSITE_ORG_ID = ENSITE_ORG.id;
const INSPECTIFY_ORG_ID = INSPECTIFY_ORG.id;

const REMI_AUTOMATIONS_USER_PERMISSION_ROLES = [
  PermissionRole.REMI_AUTOMATIONS,
  PermissionRole.REMI_USER,
  PermissionRole.REMI_SALES,
  PermissionRole.REMI_OPS,
];

const capabilitiesFor = (roles: PermissionRole[]) => expandRolesToCapabilities(roles);

export const REMI_AI_USER = {
  person: {
    firstName: "Rain",
    lastName: "",
    primaryEmailAddress: { emailAddress: "support@remihq.com" },
    primaryPhone: null,
    organizationId: "remi_internal",
    organization: {
      type: "REMI_INTERNAL",
    },
    id: "rain",
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: "rain-contact", name: "Rain" },
    coveringPersonId: null,
    permissionRoles: [PermissionRole.REMI_USER],
    regionRoles: [],
    capabilities: capabilitiesFor([PermissionRole.REMI_USER]),
    disabled: false,
  },
} satisfies ContextUser;

export const PARTNER_BOT_USER = {
  person: {
    firstName: "Partner",
    lastName: "Bot",
    primaryEmailAddress: null,
    primaryPhone: null,
    organizationId: "remi_internal",
    organization: {
      type: "REMI_INTERNAL",
    },
    id: "partner-bot",
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: "partner-bot-contact", name: "Partner Bot" },
    coveringPersonId: null,
    permissionRoles: [PermissionRole.REMI_USER],
    regionRoles: [],
    capabilities: capabilitiesFor([PermissionRole.REMI_USER]),
    disabled: false,
  },
} satisfies ContextUser;

export const UNMAPPED_QUO_SENDER_USER_ID = "unmapped-quo-sender";

// Authors a text or call a human sent from an ownerless Quo line whose Quo user has no mapped person.
// Distinct from remi-automations so those rows read as human Remi activity, not automation.
export const UNMAPPED_QUO_SENDER_USER = {
  person: {
    firstName: "Unmapped",
    lastName: "Quo Sender",
    primaryEmailAddress: null,
    primaryPhone: null,
    organizationId: "remi_internal",
    organization: {
      type: "REMI_INTERNAL",
    },
    id: UNMAPPED_QUO_SENDER_USER_ID,
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: `${UNMAPPED_QUO_SENDER_USER_ID}-contact`, name: "Unmapped Quo Sender" },
    coveringPersonId: null,
    permissionRoles: [],
    regionRoles: [],
    capabilities: capabilitiesFor([]),
    disabled: false,
  },
} satisfies ContextUser;

export const REMI_AUTOMATIONS_USER = {
  person: {
    firstName: "Remi",
    lastName: "Automations",
    primaryEmailAddress: { emailAddress: "support@remihq.com" },
    primaryPhone: null,
    organizationId: "remi_internal",
    organization: {
      type: "REMI_INTERNAL",
    },
    id: REMI_AUTOMATIONS_USER_ID,
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: `${REMI_AUTOMATIONS_USER_ID}-contact`, name: "Remi Automations" },
    coveringPersonId: null,
    permissionRoles: REMI_AUTOMATIONS_USER_PERMISSION_ROLES,
    regionRoles: [],
    capabilities: capabilitiesFor(REMI_AUTOMATIONS_USER_PERMISSION_ROLES),
    disabled: false,
  },
} satisfies ContextUser;

export const REMI_WEBSITE_USER = {
  person: {
    firstName: "Remi",
    lastName: "Website",
    primaryEmailAddress: { emailAddress: "support@remihq.com" },
    primaryPhone: null,
    organizationId: "remi_internal",
    organization: {
      type: "REMI_INTERNAL",
    },
    id: REMI_WEBSITE_USER_ID,
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: `${REMI_WEBSITE_USER_ID}-contact`, name: "Remi Website" },
    coveringPersonId: null,
    permissionRoles: [PermissionRole.REMI_USER],
    regionRoles: [],
    capabilities: capabilitiesFor([PermissionRole.REMI_USER]),
    disabled: false,
  },
} satisfies ContextUser;

export const REMI_HUBSPOT_USER = {
  person: {
    firstName: "Remi",
    lastName: "Hubspot",
    primaryEmailAddress: { emailAddress: "support@remihq.com" },
    primaryPhone: null,
    organizationId: "remi_internal",
    organization: {
      type: "REMI_INTERNAL",
    },
    id: REMI_HUBSPOT_USER_ID,
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: `${REMI_HUBSPOT_USER_ID}-contact`, name: "Remi Hubspot" },
    coveringPersonId: null,
    permissionRoles: [PermissionRole.REMI_USER],
    regionRoles: [],
    capabilities: capabilitiesFor([PermissionRole.REMI_USER]),
    disabled: false,
  },
} satisfies ContextUser;

export const SUNRUN_AUTOMATIONS_USER = {
  person: {
    firstName: "Sunrun",
    lastName: "Automations",
    primaryEmailAddress: null,
    primaryPhone: null,
    organizationId: "sunrun",
    organization: {
      type: "SOLAR_COMPANY",
    },
    id: "sunrun-automations",
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: "sunrun-automations-contact", name: "Sunrun Automations" },
    coveringPersonId: null,
    permissionRoles: [PermissionRole.BUYER_ADMIN, PermissionRole.PARTNER_DEVELOPER],
    regionRoles: [],
    capabilities: capabilitiesFor([PermissionRole.BUYER_ADMIN, PermissionRole.PARTNER_DEVELOPER]),
    disabled: false,
  },
} satisfies ContextUser;

export const WESTHILL_AUTOMATIONS_USER = {
  person: {
    firstName: "Westhill",
    lastName: "Automations",
    primaryEmailAddress: { emailAddress: "engineering+westhill@remihq.com" },
    primaryPhone: null,
    organizationId: WESTHILL_ORG_ID,
    organization: {
      type: "SOLAR_COMPANY",
    },
    id: "westhill-automations",
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: "westhill-automations-contact", name: "Westhill Automations" },
    coveringPersonId: null,
    permissionRoles: [PermissionRole.BUYER_ADMIN, PermissionRole.PARTNER_DEVELOPER],
    regionRoles: [],
    capabilities: capabilitiesFor([PermissionRole.BUYER_ADMIN, PermissionRole.PARTNER_DEVELOPER]),
    disabled: false,
  },
} satisfies ContextUser;

export const OPENLY_AUTOMATIONS_USER = {
  person: {
    firstName: "Openly",
    lastName: "Automations",
    primaryEmailAddress: null,
    primaryPhone: null,
    organizationId: OPENLY_ORG_ID,
    organization: {
      type: "SOLAR_COMPANY",
    },
    id: "openly-automations",
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: "openly-automations-contact", name: "Openly Automations" },
    coveringPersonId: null,
    permissionRoles: [PermissionRole.BUYER_ADMIN, PermissionRole.PARTNER_DEVELOPER],
    regionRoles: [],
    capabilities: capabilitiesFor([PermissionRole.BUYER_ADMIN, PermissionRole.PARTNER_DEVELOPER]),
    disabled: false,
  },
} satisfies ContextUser;

export const AMFAM_AUTOMATIONS_USER = {
  person: {
    firstName: "American Family",
    lastName: "Automations",
    primaryEmailAddress: null,
    primaryPhone: null,
    organizationId: AMFAM_ORG_ID,
    organization: {
      type: "SOLAR_COMPANY",
    },
    id: "amfam-automations",
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: "amfam-automations-contact", name: "American Family Automations" },
    coveringPersonId: null,
    permissionRoles: [PermissionRole.BUYER_ADMIN, PermissionRole.PARTNER_DEVELOPER],
    regionRoles: [],
    capabilities: capabilitiesFor([PermissionRole.BUYER_ADMIN, PermissionRole.PARTNER_DEVELOPER]),
    disabled: false,
  },
} satisfies ContextUser;

export const ENSITE_AUTOMATIONS_USER = {
  person: {
    firstName: "Ensite",
    lastName: "Automations",
    primaryEmailAddress: { emailAddress: "engineering+ensite@remihq.com" },
    primaryPhone: null,
    organizationId: ENSITE_ORG_ID,
    organization: {
      type: "CONTRACTOR",
    },
    id: "ensite-automations",
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: "ensite-automations-contact", name: "Ensite Automations" },
    coveringPersonId: null,
    permissionRoles: [PermissionRole.CONTRACTOR_ADMIN],
    regionRoles: [],
    capabilities: capabilitiesFor([PermissionRole.CONTRACTOR_ADMIN]),
    disabled: false,
  },
} satisfies ContextUser;

export const INSPECTIFY_AUTOMATIONS_USER = {
  person: {
    firstName: "Inspectify",
    lastName: "Automations",
    primaryEmailAddress: { emailAddress: "engineering+inspectify@remihq.com" },
    primaryPhone: null,
    organizationId: INSPECTIFY_ORG_ID,
    organization: {
      type: "CONTRACTOR",
    },
    id: "inspectify-automations",
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: "inspectify-automations-contact", name: "Inspectify Automations" },
    coveringPersonId: null,
    permissionRoles: [PermissionRole.CONTRACTOR_ADMIN],
    regionRoles: [],
    capabilities: capabilitiesFor([PermissionRole.CONTRACTOR_ADMIN]),
    disabled: false,
  },
} satisfies ContextUser;

export const OPEN_PHONE_USER = {
  person: {
    firstName: "OpenPhone",
    lastName: "Automations",
    primaryEmailAddress: null,
    primaryPhone: null,
    organizationId: "remi_internal",
    organization: {
      type: "REMI_INTERNAL",
    },
    id: "openphone-automations",
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: "openphone-automations-contact", name: "OpenPhone Automations" },
    coveringPersonId: null,
    permissionRoles: [PermissionRole.REMI_USER],
    regionRoles: [],
    capabilities: capabilitiesFor([PermissionRole.REMI_USER]),
    disabled: false,
  },
} satisfies ContextUser;

export const GROOVE_USER = {
  person: {
    firstName: "Groove",
    lastName: "Automations",
    primaryEmailAddress: null,
    primaryPhone: null,
    organizationId: "remi_internal",
    organization: {
      type: "REMI_INTERNAL",
    },
    id: "groove-automations",
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: "groove-automations-contact", name: "Groove Automations" },
    coveringPersonId: null,
    permissionRoles: [PermissionRole.REMI_USER],
    regionRoles: [],
    capabilities: capabilitiesFor([PermissionRole.REMI_USER]),
    disabled: false,
  },
} satisfies ContextUser;

export const SUPPORT_USER = {
  person: {
    firstName: "Remi",
    lastName: "Support",
    primaryEmailAddress: null,
    primaryPhone: {
      id: "cm0gqxr9o02yhbarh7fj05uc0",
      number: "+19169997497",
      extension: "",
      phoneNotes: null,
    },
    organizationId: "remi_internal",
    organization: {
      type: "REMI_INTERNAL",
    },
    id: "remi-support",
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: "remi-support-contact", name: "Remi Support" },
    coveringPersonId: null,
    permissionRoles: [PermissionRole.REMI_USER],
    regionRoles: [],
    capabilities: capabilitiesFor([PermissionRole.REMI_USER]),
    disabled: false,
  },
} satisfies ContextUser;

export const SALES_USER = {
  person: {
    firstName: "Remi",
    lastName: "Sales",
    primaryEmailAddress: null,
    primaryPhone: null,
    organizationId: "remi_internal",
    organization: {
      type: "REMI_INTERNAL",
    },
    id: "remi-sales",
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: "remi-sales-contact", name: "Remi Sales" },
    coveringPersonId: null,
    disabled: false,
    permissionRoles: [PermissionRole.REMI_USER],
    regionRoles: [],
    capabilities: capabilitiesFor([PermissionRole.REMI_USER]),
  },
} satisfies ContextUser;

// Shared service account behind the Notion Sales Agent. REMI_SALES_ADMIN so it can call the admin
// `bid.list` / `ticket.followUps` with an explicit `assigneeId` to read a specific rep's open
// pipeline and due follow-ups — the auditable replacement for the removed `mcp.sales.actAsRep`
// impersonation flag. The prod row + API key are minted separately by the one-off AIOPS-28 script;
// this entry keeps the account in local/preview/E2E DBs.
export const SALES_AGENT_USER = {
  person: {
    firstName: "Sales",
    lastName: "Agent",
    primaryEmailAddress: { emailAddress: "sales-agent@remihq.com" },
    primaryPhone: null,
    organizationId: "remi_internal",
    organization: {
      type: "REMI_INTERNAL",
    },
    id: "sales-agent",
    status: "ACTIVE",
    setStatusEventId: null,
    contact: { id: "sales-agent-contact", name: "Sales Agent" },
    coveringPersonId: null,
    permissionRoles: [PermissionRole.REMI_SALES_ADMIN],
    regionRoles: [],
    capabilities: capabilitiesFor([PermissionRole.REMI_SALES_ADMIN]),
    disabled: false,
  },
} satisfies ContextUser;

export const REMI_FICTIONAL_USER_IDS: Set<string> = new Set([
  SUPPORT_USER.person.id,
  REMI_AUTOMATIONS_USER.person.id,
  SALES_USER.person.id,
  REMI_AI_USER.person.id,
  SALES_AGENT_USER.person.id,
  UNMAPPED_QUO_SENDER_USER.person.id,
]);

// Distinct from REMI_FICTIONAL_USER_IDS: includes partner-org automations and
// excludes support/sales (human-operated), so a task completed by one of these
// is a programmatic completion, not human work. Merging the two sets is a bug.
export const AUTOMATION_USER_IDS: ReadonlySet<string> = new Set([
  REMI_AUTOMATIONS_USER.person.id,
  REMI_AI_USER.person.id,
  PARTNER_BOT_USER.person.id,
  REMI_WEBSITE_USER.person.id,
  REMI_HUBSPOT_USER.person.id,
  SUNRUN_AUTOMATIONS_USER.person.id,
  WESTHILL_AUTOMATIONS_USER.person.id,
  OPENLY_AUTOMATIONS_USER.person.id,
  AMFAM_AUTOMATIONS_USER.person.id,
  ENSITE_AUTOMATIONS_USER.person.id,
  INSPECTIFY_AUTOMATIONS_USER.person.id,
  OPEN_PHONE_USER.person.id,
  GROOVE_USER.person.id,
]);

export const UNASSIGNABLE_USER_IDS: ReadonlySet<string> = new Set([
  ...REMI_FICTIONAL_USER_IDS,
  ...AUTOMATION_USER_IDS,
  JOB_NIMBUS_USER_ID,
]);

export const REMI_SYSTEM_LINE_OWNER_IDS: ReadonlySet<string> = new Set([
  REMI_AUTOMATIONS_USER_ID,
  SALES_USER.person.id,
  SUPPORT_USER.person.id,
]);
